import moment from "moment";
import type { TimeFrameOption } from "@/common/state/models";
import { TimeAxisType } from "@/gql/types";

/**
 * The period Forensics' Activities Summary compares against: the calendar
 * period right before the selected one — the month before a month, the week
 * before a week.
 *
 * Task 25042: months used to take the next item in the options list, and at
 * the edge of an org's timeline that item is not the month before — the org's
 * first month compared itself with the month after it. The period is worked
 * out by date instead, and `period_has_data` says whether it holds any of the
 * org's data.
 *
 * `time_frames` is the options list, newest first: this week, last week, this
 * month, then each month back to the org's first.
 */
export const previous_period = (
  time_frame: TimeFrameOption,
  time_frames: readonly TimeFrameOption[]
): TimeFrameOption => {
  // For Week type: relative is always the previous week (same type), not a month.
  // "Current week" → "Last week", "Last week" → "Week before last"
  if (time_frame.type === TimeAxisType.Week) {
    const prev_from = moment(time_frame.from)
      .subtract(1, "week")
      .format("YYYY-MM-DD");
    // Reuse existing option if available (covers "Current week" → "Last week")
    const existing = time_frames.find((d) => d.from === prev_from);
    if (existing) return existing;
    // Synthesize a "week before last" option that isn't in the list
    const weekEnd = moment(prev_from);
    const weekStart = weekEnd.clone().isoWeekday(1);
    return {
      type: TimeAxisType.Week,
      from: prev_from,
      displayName: `Week before last (${weekStart.format("DD/MM")} - ${weekEnd.format("DD/MM")})`,
      disable: false,
    } as TimeFrameOption;
  }

  // For Month type: the calendar month before, whatever the list holds
  const prev = moment(time_frame.from).startOf("month").subtract(1, "month");
  const prev_from = prev.format("YYYY-MM-DD");
  const existing = time_frames.find(
    (d) => d.type === TimeAxisType.Month && d.from === prev_from
  );
  if (existing) return existing;
  // Before the org's first month: not offered in the list, and it holds no
  // data, so period_has_data hides it rather than showing zeros
  return {
    type: TimeAxisType.Month,
    from: prev_from,
    displayName: prev.format("MMMM YYYY"),
    disable: true,
  } as TimeFrameOption;
};

/**
 * Whether a previous period holds any of the org's data. It does not in the
 * org's first month, nor in its first week after onboarding, and the summary
 * then shows only the selected period's column.
 *
 * `data_from` is the org's first data date (`basedOn.dateFrom`); unknown, the
 * period is assumed to have data rather than hiding a column that may not
 * need hiding.
 */
export const period_has_data = (
  previous: TimeFrameOption,
  time_frames: readonly TimeFrameOption[],
  data_from?: string | number | null
): boolean => {
  // The month options run from the org's first month to this one
  if (previous.type === TimeAxisType.Month) {
    return time_frames.some(
      (d) => d.type === TimeAxisType.Month && d.from === previous.from
    );
  }

  // A week counts when it ends on or after the org's first data
  if (!data_from) return true;
  return !moment(previous.from)
    .isoWeekday(7)
    .isBefore(moment(data_from), "day");
};
